import { IDenialPaymentRow } from '../../services/denials/denial-payments';
import { formatDate } from '../../services/primitives/date-utils';
import { BaseButton } from '../base/button/button';
import { formatCurrency } from '../patient-statement/utils/statement-format.utils';
import { mdiCashMultiple, mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import * as React from 'react';
import * as S from './denials.styles';

/**
 * One remittance, opened in place under its drill-down row (FE-116, Jordan's v3 — the claim
 * timeline's payment card).
 *
 * Every line on the payment, not only the denied one: a biller reading a denial needs the lines
 * around it — the second line's `CO-45` is why the payment was paid at all. The line that carries the
 * row's code with the row's remark set is highlighted, and that code alone is red.
 */
export interface IEraCardProps {
    payment: IDenialPaymentRow;
    onOpenClaim: (payment: IDenialPaymentRow) => void;
}

const money = (value: number): string => formatCurrency(value.toFixed(2));

const dos = (from?: string, through?: string): string => {
    if (!from) {
        return '—';
    }
    const start = formatDate(from, 'MMM d');
    return through && through !== from ? `${start} – ${formatDate(through, 'MMM d')}` : start;
};

export const EraCard: React.FC<IEraCardProps> = ({ payment, onOpenClaim }) => (
    <S.EraFrame>
        <S.EraCard>
            <S.EraHead>
                <S.EraTitle>
                    <Icon path={mdiCashMultiple} size={0.8} />
                    <strong>ERA payment</strong>
                    <S.EraMuted>
                        {payment.paymentDate ? `Paid ${formatDate(payment.paymentDate, 'MMM d, yyyy')}` : 'Not dated'}
                        {payment.status ? ` · ${payment.status}` : ''}
                    </S.EraMuted>
                </S.EraTitle>
                <S.EraTotals>
                    <span>
                        Charge <strong>{money(payment.charge)}</strong>
                    </span>
                    <span>
                        Allowed <strong>{money(payment.allowed)}</strong>
                    </span>
                    <span>
                        Payer <strong>{money(payment.payerPaid)}</strong>
                    </span>
                    <span>
                        Patient <strong>{money(payment.patientResponsibility)}</strong>
                    </span>
                </S.EraTotals>
            </S.EraHead>
            <S.EraTable role="table" aria-label="Service lines">
                <S.EraRow role="row" head>
                    {['Line', 'Procedure', 'DOS', 'POS', 'Units', 'Charge', 'Allowed', 'Paid', 'Adjustments', 'Remarks'].map(
                        label => (
                            <S.EraHeadCell key={label} role="columnheader" right={['Units', 'Charge', 'Allowed', 'Paid'].includes(label)}>
                                {label}
                            </S.EraHeadCell>
                        ),
                    )}
                </S.EraRow>
                {payment.items.map((line, index) => (
                    <S.EraRow role="row" key={`${line.controlNumber || ''}:${index}`} matched={line.matched}>
                        <S.EraCell role="cell" mono muted>
                            <S.EraLineId title={line.controlNumber}>{line.controlNumber || '—'}</S.EraLineId>
                        </S.EraCell>
                        <S.EraCell role="cell" mono>
                            <strong>{line.procedureCode || '—'}</strong>
                            {line.modifierCodes.length > 0 && <S.EraMuted> {line.modifierCodes.join(' ')}</S.EraMuted>}
                        </S.EraCell>
                        <S.EraCell role="cell">{dos(line.fromDos, line.throughDos)}</S.EraCell>
                        <S.EraCell role="cell">{line.placeOfService || '—'}</S.EraCell>
                        <S.EraCell role="cell" right>
                            {line.units ?? '—'}
                        </S.EraCell>
                        <S.EraCell role="cell" right>
                            {money(line.charge)}
                        </S.EraCell>
                        <S.EraCell role="cell" right>
                            {money(line.allowed)}
                        </S.EraCell>
                        <S.EraCell role="cell" right>
                            <strong>{money(line.paid)}</strong>
                        </S.EraCell>
                        <S.EraCell role="cell">
                            {line.adjustments.length
                                ? line.adjustments.map((adjustment, i) => (
                                      <div key={i}>
                                          <S.EraCode matched={adjustment.matched}>{adjustment.code}</S.EraCode>{' '}
                                          {money(adjustment.amount)}
                                      </div>
                                  ))
                                : '—'}
                        </S.EraCell>
                        <S.EraCell role="cell" mono>
                            {line.remarkCodes.length ? line.remarkCodes.join(', ') : '—'}
                        </S.EraCell>
                    </S.EraRow>
                ))}
            </S.EraTable>
            <S.EraFoot>
                <S.EraMuted>{payment.memo ? `Memo · ${payment.memo}` : 'No memo'}</S.EraMuted>
                <S.EraIds>
                    {payment.eobId && <span>EOB {payment.eobId.slice(0, 8)}</span>}
                    <span title={payment.id}>Payment {payment.id.slice(0, 13)}&hellip;</span>
                    <BaseButton variant="outlined" size="compact" onClick={(): void => onOpenClaim(payment)}>
                        <span>Open claim</span>
                        <Icon path={mdiOpenInNew} size={0.6} />
                    </BaseButton>
                </S.EraIds>
            </S.EraFoot>
        </S.EraCard>
    </S.EraFrame>
);
